import {
  useMemo,
  useState,
} from 'react';

import { useQuery } from '@tanstack/react-query';

import {
  Alert,
  Box,
  ButtonBase,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Typography,
} from '@mui/material';

import {
  getMarginCenterCustomerPosition,
} from '../services/marginCenterService';

import CustomerPositionRankingModal
  from './CustomerPositionRankingModal';

import {
  formatMarginCenterNumber,
} from '../utils/numberFormat';

import type {
  MarginCenterComparisonMode,
} from '../types/marginCenter';

import type {
  MarginCenterCustomerPositionGroupDimension,
  MarginCenterCustomerPositionMarginPercentage,
  MarginCenterCustomerPositionParams,
  MarginCenterCustomerPositionRankMetric,
  MarginCenterCustomerPositionResponse,
  MarginCenterCustomerPositionScope,
} from '../types/customerPosition';

import type {
  MarginCenterCustomerPositionRankingMetric,
  MarginCenterCustomerPositionRankingScopeType,
} from '../types/customerPositionRanking';


interface CustomerPositionPanelProps {
  customerId: string;

  startDate?: string | null;
  endDate?: string | null;

  comparisonMode: MarginCenterComparisonMode;
  comparisonStartDate?: string | null;
  comparisonEndDate?: string | null;

  currencyCode: string | null;
}


function formatInteger(
  value: number | null | undefined,
): string {
  if (value === null || value === undefined) {
    return '—';
  }

  return formatMarginCenterNumber(
    value,
    0,
  );
}


function formatPercentage(
  value: number | null | undefined,
): string {
  if (value === null || value === undefined) {
    return '—';
  }

  return `${formatMarginCenterNumber(
    value,
    2,
  )} %`;
}


function rankValue(
  metric:
    | MarginCenterCustomerPositionRankMetric
    | null
    | undefined,
): string {
  if (
    !metric
    || metric.current_rank === null
  ) {
    return '—';
  }

  return `#${formatInteger(
    metric.current_rank,
  )} de ${formatInteger(
    metric.current_population,
  )}`;
}


function rankMovement(
  value: number | null | undefined,
): {
  text: string;
  color: string;
} {
  if (value === null || value === undefined) {
    return {
      text: 'Sin comparación de posición',
      color: '#64748b',
    };
  }

  if (value > 0) {
    return {
      text:
        `↑ ${formatInteger(
          value,
        )} ${
          value === 1
            ? 'puesto'
            : 'puestos'
        }`,
      color: '#34d399',
    };
  }

  if (value < 0) {
    return {
      text:
        `↓ ${formatInteger(
          Math.abs(value),
        )} ${
          Math.abs(value) === 1
            ? 'puesto'
            : 'puestos'
        }`,
      color: '#fb7185',
    };
  }

  return {
    text: '= sin cambio',
    color: '#94a3b8',
  };
}


function marginMedianDifference(
  metric:
    | MarginCenterCustomerPositionMarginPercentage
    | null
    | undefined,
): number | null {
  if (
    !metric
    || metric.current_value === null
    || metric.current_median === null
  ) {
    return null;
  }

  return (
    metric.current_value
    - metric.current_median
  );
}


function marginMedianText(
  metric:
    | MarginCenterCustomerPositionMarginPercentage
    | null
    | undefined,
): string {
  const difference =
    marginMedianDifference(metric);

  if (
    !metric
    || metric.current_median === null
  ) {
    return 'Mediana no disponible';
  }

  if (difference === null) {
    return `Mediana ${formatPercentage(
      metric.current_median,
    )}`;
  }

  if (difference > 0) {
    return `${formatPercentage(
      difference,
    ).replace(' %', ' pp')} sobre la mediana`;
  }

  if (difference < 0) {
    return `${formatPercentage(
      Math.abs(difference),
    ).replace(' %', ' pp')} bajo la mediana`;
  }

  return 'En la mediana';
}


function marginMedianColor(
  metric:
    | MarginCenterCustomerPositionMarginPercentage
    | null
    | undefined,
): string {
  const difference =
    marginMedianDifference(metric);

  if (difference === null || difference === 0) {
    return '#94a3b8';
  }

  return difference > 0
    ? '#34d399'
    : '#fb7185';
}


function PositionLine({
  label,
  value,
  detail,
  detailColor = '#94a3b8',
  onClick,
}: {
  label: string;
  value: string;
  detail: string;
  detailColor?: string;
  onClick: () => void;
}) {
  return (
    <ButtonBase
      onClick={onClick}
      sx={{
        width: '100%',
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',
          sm:
            'minmax(120px, 0.8fr) minmax(0, 1fr) auto',
        },
        gap: 1,
        alignItems: 'center',
        py: 1,
        px: 0.75,
        textAlign: 'left',
        borderRadius: 1.5,
        borderBottom:
          '1px solid rgba(148,163,184,0.10)',

        '&:hover': {
          bgcolor:
            'rgba(56,189,248,0.05)',
        },

        '&:last-of-type': {
          borderBottom: 'none',
        },
      }}
    >
      <Typography
        variant="body2"
        sx={{
          color: '#cbd5e1',
          fontWeight: 750,
        }}
      >
        {label}
      </Typography>

      <Box>
        <Typography
          variant="body1"
          sx={{
            color: '#f8fafc',
            fontWeight: 900,
            lineHeight: 1.2,
          }}
        >
          {value}
        </Typography>

        <Typography
          variant="caption"
          sx={{
            mt: 0.25,
            display: 'block',
            color: detailColor,
            lineHeight: 1.35,
          }}
        >
          {detail}
        </Typography>
      </Box>

      <Typography
        variant="caption"
        sx={{
          color: '#38bdf8',
          fontWeight: 800,
          whiteSpace: 'nowrap',
        }}
      >
        Ver clasificación
      </Typography>
    </ButtonBase>
  );
}


function PositionScope({
  title,
  subtitle,
  scope,
  scopeType,
  onOpenRanking,
}: {
  title: string;
  subtitle: string;
  scope: MarginCenterCustomerPositionScope;

  scopeType:
    MarginCenterCustomerPositionRankingScopeType;

  onOpenRanking: (
    metric:
      MarginCenterCustomerPositionRankingMetric,
    scope:
      MarginCenterCustomerPositionRankingScopeType,
  ) => void;
}) {
  const revenueMovement =
    rankMovement(
      scope.revenue.rank_change,
    );

  const marginMovement =
    rankMovement(
      scope.margin_amount.rank_change,
    );

  const revenueDetailParts = [
    scope.revenue.current_share_pct !== null
      ? `${formatPercentage(
          scope.revenue.current_share_pct,
        )} de los ingresos`
      : null,
    revenueMovement.text,
  ].filter(Boolean);

  const marginDetailParts = [
    scope.margin_amount
      .current_resolved_revenue_coverage_pct
      !== null
      ? `Cobertura ${formatPercentage(
          scope.margin_amount
            .current_resolved_revenue_coverage_pct,
        )}`
      : null,
    marginMovement.text,
  ].filter(Boolean);

  const marginPercentage =
    scope.margin_percentage;

  return (
    <Box
      sx={{
        minWidth: 0,
        px: 1.5,
        py: 1.25,
        borderRadius: 2.5,
        border:
          '1px solid rgba(148,163,184,0.14)',
        bgcolor:
          'rgba(15,23,42,0.30)',
      }}
    >
      <Typography
        variant="subtitle2"
        sx={{
          color: '#e2e8f0',
          fontWeight: 900,
        }}
      >
        {title}
      </Typography>

      <Typography
        variant="caption"
        sx={{
          mt: 0.2,
          display: 'block',
          color: '#64748b',
        }}
      >
        {subtitle}
      </Typography>

      <Box sx={{ mt: 0.75 }}>
        <PositionLine
          label="Facturación"
          value={rankValue(scope.revenue)}
          detail={revenueDetailParts.join(' · ')}
          detailColor={
            revenueMovement.color
          }
          onClick={() =>
            onOpenRanking(
              'revenue',
              scopeType,
            )
          }
        />

        <PositionLine
          label="Margen"
          value={rankValue(
            scope.margin_amount,
          )}
          detail={marginDetailParts.join(' · ')}
          detailColor={
            marginMovement.color
          }
          onClick={() =>
            onOpenRanking(
              'margin_amount',
              scopeType,
            )
          }
        />

        <PositionLine
          label="Margen %"
          value={
            marginPercentage.current_percentile
              !== null
              ? `Percentil ${formatMarginCenterNumber(
                  marginPercentage
                    .current_percentile,
                  1,
                )}`
              : '—'
          }
          detail={
            marginPercentage.current_median
              !== null
              ? `${marginMedianText(
                  marginPercentage,
                )} · mediana ${formatPercentage(
                  marginPercentage
                    .current_median,
                )}`
              : 'Comparación no calculable'
          }
          detailColor={
            marginMedianColor(
              marginPercentage,
            )
          }
          onClick={() =>
            onOpenRanking(
              'margin_percentage',
              scopeType,
            )
          }
        />
      </Box>
    </Box>
  );
}


export default function CustomerPositionPanel({
  customerId,
  startDate,
  endDate,
  comparisonMode,
  comparisonStartDate,
  comparisonEndDate,
  currencyCode,
}: CustomerPositionPanelProps) {
  const [
    groupDimension,
    setGroupDimension,
  ] = useState<
    MarginCenterCustomerPositionGroupDimension
    | undefined
  >(undefined);

  const [
    rankingDetail,
    setRankingDetail,
  ] = useState<{
    metric:
      MarginCenterCustomerPositionRankingMetric;

    scope:
      MarginCenterCustomerPositionRankingScopeType;
  } | null>(null);

  const params =
    useMemo<MarginCenterCustomerPositionParams>(
      () => ({
        start_date:
          startDate ?? undefined,

        end_date:
          endDate ?? undefined,

        comparison_mode:
          comparisonMode,

        comparison_start_date:
          comparisonMode === 'custom'
            ? comparisonStartDate ?? undefined
            : undefined,

        comparison_end_date:
          comparisonMode === 'custom'
            ? comparisonEndDate ?? undefined
            : undefined,

        group_dimension:
          groupDimension,
      }),
      [
        comparisonEndDate,
        comparisonMode,
        comparisonStartDate,
        endDate,
        groupDimension,
        startDate,
      ],
    );

  const positionQuery = useQuery<
    MarginCenterCustomerPositionResponse
  >({
    queryKey: [
      'margin-center',
      'customer-position',
      customerId,
      params,
    ],
    queryFn: () =>
      getMarginCenterCustomerPosition(
        customerId,
        params,
      ),
    enabled: Boolean(customerId),
    placeholderData: (previousData) =>
      previousData,
    retry: false,
  });

  const data = positionQuery.data;

  const selectedDimension =
    groupDimension
    ?? data?.selected_group?.key
    ?? '';

  return (
    <Paper
      variant="outlined"
      sx={{
        p: {
          xs: 1.5,
          md: 2,
        },
        borderColor:
          'rgba(148,163,184,0.18)',
        bgcolor:
          'rgba(15,23,42,0.32)',
        borderRadius: 3,
      }}
    >
      <Stack spacing={1.5}>
        <Stack
          direction={{
            xs: 'column',
            md: 'row',
          }}
          spacing={1.5}
          alignItems={{
            xs: 'stretch',
            md: 'flex-start',
          }}
          justifyContent="space-between"
        >
          <Box>
            <Typography
              variant="h6"
              sx={{
                color: '#f8fafc',
                fontWeight: 900,
              }}
            >
              Posición del cliente
            </Typography>

            <Typography
              variant="body2"
              sx={{
                mt: 0.2,
                color: '#94a3b8',
              }}
            >
              Cómo se sitúa dentro del negocio observado y frente a clientes con características comparables.
            </Typography>
          </Box>

          {data
            && data.available_group_dimensions
              .length > 0 && (
            <FormControl
              size="small"
              sx={{
                minWidth: 220,
                flexShrink: 0,
              }}
            >
              <InputLabel id="customer-position-group-label">
                Comparar dentro de
              </InputLabel>

              <Select
                labelId="customer-position-group-label"
                label="Comparar dentro de"
                value={selectedDimension}
                onChange={(event) =>
                  setGroupDimension(
                    event.target.value as MarginCenterCustomerPositionGroupDimension,
                  )
                }
              >
                {data.available_group_dimensions.map(
                  (dimension) => (
                    <MenuItem
                      key={dimension.key}
                      value={dimension.key}
                    >
                      {dimension.label}
                      {' · '}
                      {dimension.value}
                    </MenuItem>
                  ),
                )}
              </Select>
            </FormControl>
          )}
        </Stack>

        {positionQuery.isFetching && (
          <LinearProgress />
        )}

        {positionQuery.isError && (
          <Alert severity="error">
            No se ha podido calcular la posición de este cliente.
          </Alert>
        )}

        {!positionQuery.isError
          && data
          && (
            <>
              {data.status ===
                'no_current_activity' && (
                <Alert severity="info">
                  Este cliente no tiene actividad en el periodo actual, por lo que no dispone de posición actual.
                </Alert>
              )}

              {data.status ===
                'no_activity' && (
                <Alert severity="info">
                  Este cliente no tiene actividad en los periodos seleccionados.
                </Alert>
              )}

              {data.status === 'ok'
                && data.global && (
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: '1fr',
                      lg:
                        data.group
                          ? 'repeat(2, minmax(0, 1fr))'
                          : '1fr',
                    },
                    gap: 1.25,
                  }}
                >
                  <PositionScope
                    title="Toda la cuenta"
                    subtitle={`${formatInteger(
                      data.global.population
                        .current_active_customers,
                    )} clientes activos en el periodo`}
                    scope={data.global}
                    scopeType="global"
                    onOpenRanking={(
                      metric,
                      scope,
                    ) =>
                      setRankingDetail({
                        metric,
                        scope,
                      })
                    }
                  />

                  {data.group && (
                    <PositionScope
                      title={`${data.group.dimension.label} · ${data.group.dimension.value}`}
                      subtitle={`${formatInteger(
                        data.group.population
                          .current_active_customers,
                      )} clientes activos en este grupo`}
                      scope={data.group}
                      scopeType="group"
                      onOpenRanking={(
                        metric,
                        scope,
                      ) =>
                        setRankingDetail({
                          metric,
                          scope,
                        })
                      }
                    />
                  )}
                </Box>
              )}

              {data.selected_group
                && data.selected_group
                  .capability_status
                  === 'partial' && (
                <Typography
                  variant="caption"
                  sx={{
                    color: '#64748b',
                  }}
                >
                  Cobertura de la dimensión {data.selected_group.label.toLowerCase()}: {formatPercentage(
                    data.selected_group
                      .coverage_pct,
                  )}.
                </Typography>
              )}
            </>
          )}
      </Stack>

      {rankingDetail && (
        <CustomerPositionRankingModal
          open
          customerId={customerId}
          metric={rankingDetail.metric}
          scope={rankingDetail.scope}
          groupDimension={
            rankingDetail.scope === 'group'
              ? data?.group?.dimension.key
              : undefined
          }
          startDate={startDate}
          endDate={endDate}
          comparisonMode={comparisonMode}
          comparisonStartDate={
            comparisonStartDate
          }
          comparisonEndDate={
            comparisonEndDate
          }
          currencyCode={currencyCode}
          onClose={() =>
            setRankingDetail(null)
          }
        />
      )}
    </Paper>
  );
}
